import { useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import API_URL from '../constants/environment';

export type Role = 'executive' | 'central-parts' | 'parts-manager' | 'gm' | 'estimator' | 'other' | 'it' | 'invoicing' | 'technician';
export type WebsiteTab = 'actvity' | 'dashboard' | 'invoicing' | 'inventory' | 'reporting' | 'ask-carl' | 'ro-manager' | 'tasks';
export type Permission = (
    'announcements' |
    'place-order' |
    'place-ev-order' |
    'order-flagged-parts' |
    'change-selections' |
    'change-unflagged' |
    'clear-flag' |
    'add-option-note' |
    'complete-uncomplete' |
    'archive-unarchive' |
    'request-quotes' |
    'update-order-details' |
    'include-exclude-parts' |
    'toggle-flag' |
    'flag-order' |
    'manual-rekey-action' |
    'export-selections' |
    'accept-supplier-changes' |
    'general-reporting' |
    'invoicing-reporting' |
    'inventory-reporting' |
    'it-settings' |
    'settings' |
    'preferences' |
    'invoicing' |
    'inventory'
);

interface ValidSessionResponse {
    sessionExists: true;
    sessionId: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    position: string;
    username: string;
    isAdmin: boolean;
    locationId: Array<string> | 'all';
    organizationId: string;
    organizationName: string;
    organizationMode: 'full-time' | 'invoicing' | 'inventory';
    rekeyingEnabled: boolean;
    pylonEmailHash: string;
    locationName: string;
    onboardingStep: string | null;
    tabs: Array<WebsiteTab>;
    permissions: Array<Permission>;
    demoMode?: boolean;
    inventoryEnabled: boolean;
    invoicingEnabled: boolean;
    deliveryReceiveEnabled?: boolean;
    deliveryReceiveEnabledAt?: string | null;
    askCarlAllowed?: boolean;
    /** Yaxle employee account: can switch between every customer organization. */
    isInternal?: boolean;
    /** Employee who may invite employees and change their access (Settings → Yaxle → Team). */
    canManageInternalTeam?: boolean;
}

interface InvalidSessionResponse {
    sessionExists: false;
}

type SessionValidationResponse = InvalidSessionResponse | ValidSessionResponse;

export const validateSession = async (sessionId: string | null): Promise<SessionValidationResponse> => {
    try {
        const { data }: AxiosResponse<ValidSessionResponse> = await axios.post(
            `${API_URL}/account/validate`,
            {
                sessionId: sessionId
            }
        );
        return { ...data, sessionExists: true };
    } catch {
        return {
            sessionExists: false,
        };
    }
};

export const useValidateSession = (sessionId: string | null) => {
    return useQuery({
        queryKey: ['/account/validate', sessionId],
        queryFn: () => validateSession(sessionId),
        enabled: !!sessionId,
        retry: false,
        staleTime: 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
}; 